import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type Updater,
} from "@tanstack/react-table";
import { createContext, useContext, useState, type ReactNode } from "react";
import { useDebouncedCallback } from "use-debounce";

import { TableSearchParamsBackend } from "./TableSearchParamsBackend";
import { TableSearchStateBackend } from "./TableSearchStateBackend";

const RESERVED_KEYS = new Set(["q", "p", "ps"]);

export type ExtraFilters = Record<string, unknown>;

export interface TableQueryParams {
  q: string;
  pageIndex: number;
  pageSize: number;
  extraFilters: ExtraFilters;
}

export interface TableSearchContextValue {
  q: string;
  pageIndex: number;
  pageSize: number;
  pagination: PaginationState;
  extraFilters: ExtraFilters;
  queryParams: TableQueryParams;
  isInitialQueryParams: boolean;
  setSearch: (value: string) => void;
  onPaginationChange: OnChangeFn<PaginationState>;
  setExtraFilter: (key: string, value: unknown) => void;
  setExtraFilters: (partial: Record<string, unknown>) => void;
}

export const TableSearchContext = createContext<TableSearchContextValue | null>(
  null,
);

export interface TableSearchProviderProps {
  defaultPageSize: number;
  searchDebounceMs?: number;
  backend?: "url" | "state";
  children: ReactNode;
}

interface TableSearchApiOptions {
  defaultPageSize: number;
  searchDebounceMs?: number;
}

function pickExtraFilters(search: Record<string, unknown>): ExtraFilters {
  const result: ExtraFilters = {};

  for (const key of Object.keys(search)) {
    if (!RESERVED_KEYS.has(key) && search[key] !== undefined) {
      result[key] = search[key];
    }
  }

  return result;
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;

  if (Array.isArray(value) && value.length === 0) return true;

  return false;
}

export function useSearchParamsApi(
  props: TableSearchApiOptions,
): TableSearchContextValue {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  const urlQ = (search.q as string | undefined) ?? "";
  const pageIndex = (search.p as number | undefined) ?? 0;
  const pageSize = (search.ps as number | undefined) ?? props.defaultPageSize;
  const extraFilters = pickExtraFilters(search);

  const debouncedNavigateQ = useDebouncedCallback((value: string) => {
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        return { ...prev, q: value || undefined, p: undefined };
      },
      replace: true,
    });
  }, props.searchDebounceMs ?? 300);

  const queryParams: TableQueryParams = {
    q: urlQ,
    pageIndex,
    pageSize,
    extraFilters,
  };

  const [initialQueryParams] = useState(() => {
    return queryParams;
  });

  const isInitialQueryParams =
    JSON.stringify(queryParams) === JSON.stringify(initialQueryParams);

  function setSearch(value: string) {
    debouncedNavigateQ(value);
  }

  function onPaginationChange(updater: Updater<PaginationState>) {
    const current: PaginationState = { pageIndex, pageSize };
    const next = typeof updater === "function" ? updater(current) : updater;
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        return {
          ...prev,
          p: next.pageIndex > 0 ? next.pageIndex : undefined,
          ps:
            next.pageSize !== props.defaultPageSize ? next.pageSize : undefined,
        };
      },
    });
  }

  function setExtraFilter(key: string, value: unknown) {
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        return {
          ...prev,
          [key]: isEmptyValue(value) ? undefined : value,
          p: undefined,
        };
      },
      replace: true,
    });
  }

  function setExtraFilters(partial: Record<string, unknown>) {
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev };

        for (const key of Object.keys(partial)) {
          next[key] = isEmptyValue(partial[key]) ? undefined : partial[key];
        }

        next.p = undefined;
        return next;
      },
      replace: true,
    });
  }

  const value: TableSearchContextValue = {
    q: urlQ,
    pageIndex,
    pageSize,
    pagination: { pageIndex, pageSize },
    extraFilters,
    queryParams,
    isInitialQueryParams,
    setSearch,
    onPaginationChange,
    setExtraFilter,
    setExtraFilters,
  };

  return value;
}

export function useStateApi(
  props: TableSearchApiOptions,
): TableSearchContextValue {
  const [debouncedQ, setDebouncedQ] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(props.defaultPageSize);
  const [extraFilters, setExtraFiltersState] = useState<ExtraFilters>({});

  const debouncedSetQ = useDebouncedCallback((value: string) => {
    setDebouncedQ(value);
  }, props.searchDebounceMs ?? 300);

  const queryParams: TableQueryParams = {
    q: debouncedQ,
    pageIndex,
    pageSize,
    extraFilters,
  };

  const [initialQueryParams] = useState(() => {
    return queryParams;
  });

  const isInitialQueryParams =
    JSON.stringify(queryParams) === JSON.stringify(initialQueryParams);

  function setSearch(value: string) {
    setPageIndex(0);
    debouncedSetQ(value);
  }

  function onPaginationChange(updater: Updater<PaginationState>) {
    const current: PaginationState = { pageIndex, pageSize };
    const next = typeof updater === "function" ? updater(current) : updater;
    setPageIndex(next.pageIndex);
    setPageSize(next.pageSize);
  }

  function setExtraFilter(key: string, value: unknown) {
    setExtraFiltersState((prev) => {
      const next: ExtraFilters = { ...prev };

      if (isEmptyValue(value)) {
        delete next[key];
      } else {
        next[key] = value;
      }

      return next;
    });
    setPageIndex(0);
  }

  function setExtraFilters(partial: Record<string, unknown>) {
    setExtraFiltersState((prev) => {
      const next: ExtraFilters = { ...prev };

      for (const key of Object.keys(partial)) {
        if (isEmptyValue(partial[key])) {
          delete next[key];
        } else {
          next[key] = partial[key];
        }
      }

      return next;
    });
    setPageIndex(0);
  }

  const value: TableSearchContextValue = {
    q: debouncedQ,
    pageIndex,
    pageSize,
    pagination: { pageIndex, pageSize },
    extraFilters,
    queryParams,
    isInitialQueryParams,
    setSearch,
    onPaginationChange,
    setExtraFilter,
    setExtraFilters,
  };

  return value;
}

export function TableSearchProvider(props: TableSearchProviderProps) {
  if (props.backend === "state") {
    return <TableSearchStateBackend {...props} />;
  }

  return <TableSearchParamsBackend {...props} />;
}

export function useTableSearch() {
  const ctx = useContext(TableSearchContext);

  if (!ctx) {
    throw new Error("useTableSearch must be used within TableSearchProvider");
  }

  return ctx;
}

export interface TableColumnFilterKey {
  columnId: string;
  searchKey?: string;
}

export function useTableSearchColumnFilters(keys: TableColumnFilterKey[]) {
  const tableSearch = useTableSearch();

  const columnFilters: ColumnFiltersState = keys.flatMap((key) => {
    const value = tableSearch.extraFilters[key.searchKey ?? key.columnId];

    if (Array.isArray(value) && value.length > 0) {
      return [{ id: key.columnId, value }];
    }

    return [];
  });

  function onColumnFiltersChange(updater: Updater<ColumnFiltersState>) {
    const next =
      typeof updater === "function" ? updater(columnFilters) : updater;
    tableSearch.setExtraFilters(
      Object.fromEntries(
        keys.map((key) => {
          return [
            key.searchKey ?? key.columnId,
            next.find((filter) => {
              return filter.id === key.columnId;
            })?.value,
          ];
        }),
      ),
    );
  }

  return { columnFilters, onColumnFiltersChange };
}
